/** Command guard types (docs/specs/command-guard.md §4.1). */

export type GuardVerdict = "allow" | "block" | "ask";

/** Every rule in the v1 table (§2), in table order. */
export const GUARD_RULE_IDS = [
  "credential.read",
  "runtime.integrity",
  "port.live-hub",
  "proc.kill-broad",
  "proc.kill-foreign",
  "git.push",
  "git.shared-state",
  "git.branch-switch",
  "git.config",
  "rm.outside-worktree",
  "rm.git-dir",
  "net.unknown-host",
  "git.history-rewrite",
  "shell.opaque",
] as const;
export type GuardRuleId = (typeof GUARD_RULE_IDS)[number];

/** The live hub/node ports (4317 hub, 4320-4322 node and friends). */
export const GUARD_LIVE_PORTS: readonly number[] = [4317, 4320, 4321, 4322];

/** Hosts that never trip `net.unknown-host`; `ctx.allowedHosts` adds to these. */
export const GUARD_DEFAULT_ALLOWED_HOSTS: readonly string[] = ["localhost", "127.0.0.1", "::1", "registry.npmjs.org"];

/** Commands longer than this are not lexed at all; they get a `shell.opaque` hit. */
export const GUARD_MAX_COMMAND_LENGTH = 64 * 1024;

export interface GuardContext {
  /** The hook's cwd; relative paths resolve against it. */
  cwd: string;
  worktree: string;
  gitCommonDir: string;
  tmpdir: string;
  home: string;
  /** The run's branch, the only one the node pushes. */
  branch: string;
  /** What the run branched from, e.g. `origin/main` or a sha. */
  baseRef: string;
  ownPids: ReadonlySet<number>;
  sandboxed: boolean;
  /** Extra hosts on top of `GUARD_DEFAULT_ALLOWED_HOSTS` (node.json `guardAllowedHosts`). A
   * leading `*.` or `.` matches any subdomain. */
  allowedHosts: readonly string[];
  livePorts: readonly number[];
  /** Files/directories that a run must not mutate (the remote hook and its settings). */
  protectedWritePaths?: readonly string[];
  /** Local branches and `origin/*`, read once per run (§2 false-positive refinements). When absent,
   * a bare `git checkout X` is treated as a branch switch only when X looks like a branch name the
   * run is likely to see (see `looksLikeKnownRef` in classify.ts). */
  refs?: readonly string[];
  /** The base commit sha, when `baseRef` is a branch name. */
  baseSha?: string;
}

export interface GuardHit {
  rule: GuardRuleId;
  verdict: GuardVerdict;
  reason: string;
  /** The simple command the rule fired on. */
  segment: string;
}

export interface GuardResult {
  verdict: GuardVerdict;
  hits: GuardHit[];
  commandHash: string;
}
